/**
 * PATH: packages/bills/src/components/billsListLeaves.tsx
 * FILE_ID: BILLS-PKG-CMP-LIST-LEAVES-001
 * PURPOSE: Pure-presentational leaves + tiny pure helpers for the bills
 *          left-rail list panel — status/paid/due chips, date + statement
 *          section headers, skeleton / empty / error states, and the
 *          currency/date formatters.
 *
 * WHY: BillsListPanel.tsx crossed the 1000-line file-size ratchet with the
 *      information-rich row redesign. These leaves are self-contained
 *      (props in → JSX out, no panel state), so lifting them keeps the
 *      panel focused on orchestration — state, keyboard nav, windowing —
 *      without scattering tightly-coupled logic.
 *
 * DEPENDENCIES: react, lucide-react, @quotech/ui, ../types,
 *               ./billsListGrouping (RenderItem), ./virtualized-list
 *               (WindowRowProps).
 *
 * RELATED:
 *   - BillsListPanel.tsx (sole consumer)
 */

import {
  AlertCircle,
  AlertTriangle,
  CheckCircle2,
  Clock,
  FileText,
  Inbox,
} from 'lucide-react';
import { Button } from '@quotech/ui';
import type { BillListRow, BillStatus } from '../types';
import type { RenderItem } from './billsListGrouping';
import type { WindowRowProps } from './virtualized-list';

/** Due-date display state for a row: tone + ready-to-render label. */
export interface DueMeta {
  tone: 'overdue' | 'soon' | 'normal';
  label: string;
}

/**
 * Derive the due-date treatment for a row. Unlike the old DueChip (which
 * hid anything further than 7 days out), the due date is ALWAYS surfaced
 * when known — "when is this payable?" is core triage information. Paid
 * bills never show urgency (nothing is on fire once it's paid).
 *
 * pure: BillListRow → DueMeta | null
 */
export function dueMeta(row: BillListRow): DueMeta | null {
  if (!row.due_date) return null;
  const d = new Date(row.due_date);
  if (Number.isNaN(d.getTime())) return null;
  const today = new Date();
  today.setHours(0, 0, 0, 0);
  const due = new Date(d);
  due.setHours(0, 0, 0, 0);
  const days = Math.round((due.getTime() - today.getTime()) / 86_400_000);
  const paid = row.payment_status === 'paid';
  if (!paid && days < 0) {
    const overdueDays = Math.abs(days);
    // Beyond a year, "3568d overdue" stops being readable — switch to
    // years so ancient rows (real prod data goes back to 2016) still
    // communicate magnitude at a glance.
    const label =
      overdueDays >= 365
        ? `${Math.floor(overdueDays / 365)}y+ overdue`
        : `${overdueDays}d overdue`;
    return { tone: 'overdue', label };
  }
  if (!paid && days <= 7) {
    return {
      tone: 'soon',
      label: days === 0 ? 'Due today' : `Due in ${days}d`,
    };
  }
  return { tone: 'normal', label: `Due ${formatShortDate(row.due_date)}` };
}

/** Whether a row counts toward the header's overdue roll-up. */
export function isOverdue(row: BillListRow): boolean {
  return dueMeta(row)?.tone === 'overdue';
}

export function DueDateLabel({ due }: { due: DueMeta }) {
  if (due.tone === 'overdue') {
    return (
      <span className="flex shrink-0 items-center gap-1 text-xs font-semibold tabular-nums text-rose-600 dark:text-rose-400">
        <AlertCircle className="h-3 w-3" aria-hidden />
        {due.label}
      </span>
    );
  }
  if (due.tone === 'soon') {
    return (
      <span className="flex shrink-0 items-center gap-1 text-xs font-medium tabular-nums text-amber-600 dark:text-amber-400">
        <Clock className="h-3 w-3" aria-hidden />
        {due.label}
      </span>
    );
  }
  return (
    <span className="shrink-0 text-xs tabular-nums text-muted-foreground">
      {due.label}
    </span>
  );
}

export function PaidChip() {
  return (
    <span className="inline-flex shrink-0 items-center gap-1 rounded-full bg-emerald-100 px-2 py-0.5 text-[10px] font-medium text-emerald-700 dark:bg-emerald-500/15 dark:text-emerald-300">
      <CheckCircle2 className="h-2.5 w-2.5" aria-hidden />
      Paid
    </span>
  );
}

export function FromStatementPill() {
  return (
    <span
      className="shrink-0 rounded-full bg-violet-100 px-1.5 py-0.5 text-[9px] font-medium uppercase tracking-wider text-violet-800 dark:bg-violet-900/40 dark:text-violet-200"
      title="One line item from a multi-invoice statement / remittance. Right-click to reclassify the whole envelope."
    >
      from stmt
    </span>
  );
}

/**
 * Date-bucket section label ("Today" / "Yesterday" / …) — plain text, not
 * an option, skipped by keyboard nav exactly like statement headers.
 */
export function DateHeader({
  label,
  windowProps,
}: {
  label: string;
  /** When set (windowed path), positions + measures this header. */
  windowProps?: WindowRowProps | null;
}) {
  return (
    <li
      role="presentation"
      ref={windowProps?.ref}
      data-index={windowProps?.['data-index']}
      style={windowProps?.style}
      className="px-2 pb-0.5 pt-2.5 first:pt-1"
    >
      <div className="text-[10px] font-semibold uppercase tracking-wider text-muted-foreground">
        {label}
      </div>
    </li>
  );
}

export function GroupHeader({
  header,
  windowProps,
}: {
  header: Extract<RenderItem, { kind: 'header' }>;
  /** When set (windowed path), positions + measures this header. Null otherwise. */
  windowProps?: WindowRowProps | null;
}) {
  return (
    <li
      role="presentation"
      ref={windowProps?.ref}
      data-index={windowProps?.['data-index']}
      style={windowProps?.style}
      className="px-1 pt-1.5"
    >
      <div className="flex items-center justify-between gap-2 rounded-lg bg-violet-50/70 px-3 py-1.5 dark:bg-violet-950/30">
        <span className="flex min-w-0 items-center gap-1.5 text-[10px] font-semibold uppercase tracking-wider text-violet-900 dark:text-violet-200">
          <FileText className="h-3 w-3 shrink-0" aria-hidden />
          <span className="truncate">
            From statement · {header.vendorName || 'unknown vendor'}
          </span>
          <span className="shrink-0 rounded-full bg-violet-200/70 px-1.5 text-[9px] tabular-nums text-violet-900 dark:bg-violet-800/50 dark:text-violet-100">
            {header.count} {header.count === 1 ? 'item' : 'items'}
          </span>
        </span>
        {header.total > 0 && (
          <span className="shrink-0 text-[10px] font-semibold tabular-nums text-violet-900 dark:text-violet-200">
            {formatCurrency(header.total, header.currency)}
          </span>
        )}
      </div>
    </li>
  );
}

// Status → soft rounded-full pill classes. Consistent across the row list so
// the same status reads the same colour everywhere, in BOTH light and dark. The
// accent (``extracted``) uses the semantic primary token so it tracks the app
// theme; the AP pipeline climbs muted → primary → emerald; failures/quarantine
// are a soft red; the fail-closed review queues are amber (needs attention, not
// a hard error). Exhaustive Record so a new BillStatus fails the typecheck
// until it's mapped.
const STATUS_PILL: Record<BillStatus, string> = {
  pending: 'bg-muted text-muted-foreground',
  extracted: 'bg-primary/10 text-primary',
  reviewed: 'bg-emerald-100 text-emerald-700 dark:bg-emerald-500/15 dark:text-emerald-300',
  pushed: 'bg-emerald-100 text-emerald-700 dark:bg-emerald-500/15 dark:text-emerald-300',
  failed: 'bg-red-100 text-red-700 dark:bg-red-500/15 dark:text-red-300',
  quarantined: 'bg-red-100 text-red-700 dark:bg-red-500/15 dark:text-red-300',
  awaiting_document: 'bg-amber-100 text-amber-700 dark:bg-amber-500/15 dark:text-amber-300',
  needs_fetch: 'bg-amber-100 text-amber-700 dark:bg-amber-500/15 dark:text-amber-300',
  needs_review: 'bg-amber-100 text-amber-700 dark:bg-amber-500/15 dark:text-amber-300',
};

const STATUS_LABEL: Record<BillStatus, string> = {
  pending: 'Pending',
  extracted: 'Extracted',
  reviewed: 'Reviewed',
  pushed: 'Pushed',
  failed: 'Failed',
  quarantined: 'Quarantined',
  awaiting_document: 'Awaiting doc',
  needs_fetch: 'Needs fetch',
  needs_review: 'Needs review',
};

export function StatusPill({ status }: { status: BillStatus }) {
  return (
    <span
      className={
        'shrink-0 rounded-full px-2 py-0.5 text-xs font-medium ' +
        STATUS_PILL[status]
      }
    >
      {STATUS_LABEL[status]}
    </span>
  );
}

/**
 * Skeleton placeholder shown on first load (no data yet). Mirrors the
 * BillRow layout — vendor/amount over reference/due over a chips line — so
 * the list doesn't visibly reflow when real rows replace it.
 * Pure-presentational; `aria-hidden` + a polite status label keep it quiet
 * for screen readers (the surrounding region announces "Loading bills").
 */
export function BillListSkeleton({ rows = 8 }: { rows?: number }) {
  return (
    <div role="status" aria-label="Loading bills" className="animate-pulse">
      <span className="sr-only">Loading bills…</span>
      <ul aria-hidden="true">
        {Array.from({ length: rows }).map((_, i) => (
          <li key={i} className="px-1 py-0.5">
            <div className="flex items-center gap-3 rounded-xl border border-l-[3px] border-border/60 border-l-border px-3 py-2.5">
              <div className="h-8 w-8 shrink-0 rounded-xl bg-muted" />
              <div className="flex flex-1 flex-col gap-1.5">
                <div className="flex items-center justify-between gap-2">
                  <div className="h-3.5 w-32 rounded bg-muted" />
                  <div className="h-4 w-16 rounded bg-muted" />
                </div>
                <div className="flex items-center justify-between gap-2">
                  <div className="h-3 w-24 rounded bg-muted/60" />
                  <div className="h-3 w-14 rounded bg-muted/60" />
                </div>
                <div className="flex items-center gap-1.5 pt-0.5">
                  <div className="h-4 w-16 rounded-full bg-muted/70" />
                  <div className="h-4 w-20 rounded-full bg-muted/70" />
                </div>
              </div>
            </div>
          </li>
        ))}
      </ul>
    </div>
  );
}

/**
 * Empty state — no rows match the current filter/search. An icon plus a short
 * headline + hint reads as a deliberate "nothing here yet" rather than a blank
 * panel that looks like a load failure.
 */
export function EmptyState() {
  return (
    <div className="flex flex-col items-center gap-2 p-8 text-center">
      <span
        aria-hidden
        className="flex h-11 w-11 items-center justify-center rounded-2xl bg-muted text-muted-foreground"
      >
        <Inbox className="h-5 w-5" />
      </span>
      <p className="text-sm font-medium text-foreground">No bills here</p>
      <p className="max-w-[15rem] text-xs text-muted-foreground">
        Nothing matches this filter or search. Try a different status tab or
        clear your search.
      </p>
    </div>
  );
}

export function ListMessage({
  children,
  tone,
  onRetry,
}: {
  children: React.ReactNode;
  tone?: 'error';
  /** When provided (typically on the error state), render an inline Retry
   *  button so a transient load failure is recoverable without leaving the
   *  panel — the dead-end error text was a production-readiness gap. */
  onRetry?: () => void;
}) {
  const isError = tone === 'error';
  return (
    <div
      role={isError ? 'alert' : undefined}
      className={
        'flex flex-col items-center gap-3 p-6 text-center text-sm ' +
        (isError ? 'text-destructive' : 'text-muted-foreground')
      }
    >
      {isError && (
        <span
          aria-hidden
          className="flex h-11 w-11 items-center justify-center rounded-2xl bg-destructive/10 text-destructive"
        >
          <AlertTriangle className="h-5 w-5" />
        </span>
      )}
      <span>{children}</span>
      {onRetry && (
        <Button variant="outline" size="sm" onClick={onRetry}>
          Retry
        </Button>
      )}
    </div>
  );
}

export function formatShortDate(iso: string): string {
  const d = new Date(iso);
  if (Number.isNaN(d.getTime())) return '';
  return d.toLocaleDateString(undefined, { day: '2-digit', month: 'short' });
}

export function formatCurrency(
  amount: string | number | null | undefined,
  currency: string | null | undefined,
): string {
  if (amount == null || amount === '') return '—';
  const numeric = typeof amount === 'string' ? Number(amount) : amount;
  if (!Number.isFinite(numeric)) return String(amount);
  try {
    return new Intl.NumberFormat(undefined, {
      style: 'currency',
      currency: currency || 'AUD',
      maximumFractionDigits: 2,
    }).format(numeric);
  } catch {
    return `${currency || ''} ${numeric.toFixed(2)}`;
  }
}

/** Whole-dollar variant for the header roll-up — cents are noise there. */
export function formatCurrencyWhole(amount: number, currency: string): string {
  try {
    return new Intl.NumberFormat(undefined, {
      style: 'currency',
      currency: currency || 'AUD',
      maximumFractionDigits: 0,
    }).format(amount);
  } catch {
    return `${currency || ''} ${Math.round(amount)}`;
  }
}
